import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { usePricingStore } from '../../store/pricingStore';
import {
  HomeIcon, DocumentTextIcon, Squares2X2Icon,
  WrenchIcon, Cog6ToothIcon, ChevronDownIcon, ChevronRightIcon,
  PhotoIcon, MagnifyingGlassIcon, ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

interface MenuItem {
  label: string;
  path?: string;
  icon: React.ReactNode;
  children?: { label: string; path: string }[];
  adminOnly?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();
  const [toolboxOpen, setToolboxOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const openPricingModal = usePricingStore((s) => s.openPricingModal);

  const hasSubscription = user?.subscription_expiry && new Date(user.subscription_expiry) > new Date();
  const daysLeft = user?.subscription_expiry
    ? Math.max(0, Math.ceil((new Date(user.subscription_expiry).getTime() - Date.now()) / 86400000))
    : 0;
  const isActive = (path: string) => location.pathname.startsWith(path);

  const menuItems: MenuItem[] = [
    { label: '首页', path: '/home', icon: <HomeIcon className="w-5 h-5" /> },
    { label: '智能编写', path: '/projects', icon: <DocumentTextIcon className="w-5 h-5" /> },
    { label: '模板库', path: '/templates', icon: <Squares2X2Icon className="w-5 h-5" /> },
    {
      label: '工具箱', icon: <WrenchIcon className="w-5 h-5" />,
      children: [
        { label: '媒体库', path: '/media' },
        { label: '模型配置', path: '/models' },
        { label: '我的技能', path: '/skills' },
        { label: '搜索配置', path: '/search-config' },
      ],
    },
    ...(isAdmin ? [
      { label: '管理后台', path: '/admin', icon: <Cog6ToothIcon className="w-5 h-5" />, adminOnly: true },
    ] : []),
  ];

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  return (
    <aside className={`app-sidebar ${collapsed ? 'collapsed' : ''}`}>
      {/* Logo */}
      <Link to="/home" className="sidebar-logo" style={{ textDecoration: 'none' }}>
        <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="小灵标" className="logo-icon-img" />
        {!collapsed && <span className="logo-text">小灵标</span>}
      </Link>

      {/* 菜单 */}
      <nav className="sidebar-nav">
        {menuItems.map((item, idx) => {
          if (item.children) {
            return (
              <div key={idx}>
                <div
                  className={`sidebar-item ${isActive('/tools') || isActive('/media') || isActive('/models') || isActive('/search-config') ? 'active' : ''}`}
                  onClick={() => setToolboxOpen(!toolboxOpen)}
                >
                  <span className="sidebar-icon">{item.icon}</span>
                  {!collapsed && (
                    <>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {toolboxOpen ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
                    </>
                  )}
                </div>
                <div className={`sidebar-submenu ${toolboxOpen ? 'open' : ''}`}>
                  {item.children.map((child, cidx) => (
                    <Link
                      key={cidx}
                      to={child.path}
                      className={`sidebar-item ${isActive(child.path) ? 'active' : ''}`}
                      style={{ textDecoration: 'none' }}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </div>
            );
          }
          return item.adminOnly ? (
            <a
              key={idx}
              href={item.path || '/'}
              target="_blank"
              rel="noopener noreferrer"
              className={`sidebar-item ${item.path && isActive(item.path) ? 'active' : ''}`}
              style={{ textDecoration: 'none', position: 'relative' }}
            >
              <span className="sidebar-icon">{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </a>
          ) : (
            <Link
              key={idx}
              to={item.path || '/'}
              className={`sidebar-item ${item.path && isActive(item.path) ? 'active' : ''}`}
              style={{ textDecoration: 'none', position: 'relative' }}
            >
              <span className="sidebar-icon">{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* 用户信息 */}
      <div className="sidebar-user">
        <div className="user-avatar">
          {user?.username?.[0]?.toUpperCase() || 'U'}
        </div>
        {!collapsed && (
          <div className="user-info">
            <div
              className="user-name clickable"
              onClick={() => navigate('/profile')}
              title="个人设置"
            >
              {user?.username || '用户'}
            </div>
            <div
              className={`user-role${isAdmin ? '' : ' clickable'}`}
              onClick={isAdmin ? undefined : openPricingModal}
              title={isAdmin ? undefined : '点击充值/续费'}
            >
              {isAdmin ? '管理员' : (hasSubscription ? `${daysLeft}天` : '免费版')}
            </div>
          </div>
        )}
        {!collapsed && (
          <button
            onClick={handleLogout}
            className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
            title="退出登录"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
